'use client';

import React, { useEffect, useState } from 'react';
import { useSurveyStore } from '@/stores/survey/store';
import { SurveyBlock } from '@/types/survey';
import { useErrorStore } from '@/stores/profile/errorStore';
import { useToast } from '@/hooks/use-toast';
import useVerifyUser from '@/hooks/profile/useVerifyUser';
import LoaderSpin from '@/components/LoaderSpin';
import StepIndicator from '@/components/(features)/surveyWizard/layout/StepIndicators';
import { sendToWebhook } from '@/components/(features)/surveyWizard/utils/webhook';
import { getStepTitles } from '@/components/(features)/surveyWizard/utils/stepTitles';
import { ExtraStaticStep } from '@/components/(features)/surveyWizard/layout/ExtraStaticStep';
import { useErrorToast } from '@/components/(features)/surveyWizard/utils/useErrorToast';
import { handleConfirmRegistration } from '@/components/(features)/surveyWizard/utils/handleConfirmRegistration';
import SurveyStepRenderer from '@/components/(features)/surveyWizard/layout/SurveyStepRenderer';
import { ExtraStaticStep2 } from '@/components/(features)/surveyWizard/(wizards)/component-2/ExtraStaticStep2';
import { updateUser } from '@/utils/profile/updateUser';
import { LayoutProps } from '@/types/generalSettings';
import {
    PopupTracking,
    popupLandingSlug,
    popupReferrer,
} from '@/components/(features)/surveyWizard/utils/popupTracking';
import ShowGroupFieldsBeforeMobile = LayoutProps.ShowGroupFieldsBeforeMobile;

export interface SurveyRendererProps {
    showMobileInputField?: boolean;
    tracking?: PopupTracking;
    survey?: Array<{
        block: SurveyBlock[];
    }>;
    showGroupFieldsBeforeMobile?: ShowGroupFieldsBeforeMobile;
    percent?: any;
    timer?: any;
    selectedWizard?: string | undefined;
    handleConfirmClosePopup?: () => void;
}

const SurveyRenderer: React.FC<SurveyRendererProps> = ({
    survey,
    tracking,
    showGroupFieldsBeforeMobile,
    showMobileInputField,
    percent,
    timer,
    selectedWizard,
    handleConfirmClosePopup,
}) => {
    const hasExtraStep = !showMobileInputField;
    const stepTitles = getStepTitles(survey).slice(0, -1);

    const { isLoggedInGlobal, mobile, isLoading, user } = useVerifyUser();
    const { currentStep, setCurrentStep, setAnswer, answers } = useSurveyStore();
    const [phone, setPhone] = useState<string>(user?.mobile ?? '');
    const [fullName, setFullName] = useState<string>(user?.firstName ?? '');
    const [email, setEmail] = useState<string>(user?.email ?? '');
    const [isEditing, setIsEditing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [hasRegistered, setHasRegistered] = useState(false);

    const { error, clearError } = useErrorStore();
    const { toast } = useToast();

    useErrorToast(error, clearError, toast);

    // Sync phone, name, email from server user data
    useEffect(() => {
        if (isLoggedInGlobal && mobile) {
            setPhone(user?.mobile ?? '');
            setEmail(user?.email ?? '');
            setFullName(user?.firstName ?? '');
            setIsEditing(false);
        }
    }, [isLoggedInGlobal, mobile, user]);

    // The page address sent with the sign-up (keeps the visitor's own UTMs; see popupTracking).
    const urlWithoutProtocol = popupReferrer(tracking);
    // console.log(urlWithoutProtocol);

    // Handle answer submission and step change
    const handleStepButtonClick = (stepId: string, answer: string, isFinish: boolean = false) => {
        setAnswer(stepId, answer);
        if (isFinish) {
            sendToWebhook({
                answers,
                phone,
                timestamp: new Date().toISOString(),
            });
            setAnswer(stepId, '');
        } else {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePreviousButtonClick = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    // If they just registered (and this is the “mobile” step), advance them—
    // BUT now “mobile step” is step 1 if hasExtraStep, otherwise step 0
    useEffect(() => {
        const mobileStepIndex = hasExtraStep ? 1 : 0;
        if (hasRegistered && currentStep === mobileStepIndex) {
            setCurrentStep(mobileStepIndex + 1);
        }
    }, [hasRegistered, currentStep, setCurrentStep, hasExtraStep]);

    if (!survey || survey.length === 0) {
        return null;
    }

    const totalSteps = survey.length + (hasExtraStep ? 2 : 1);
    // progressPercentage formula: (currentStep / (totalSteps - 1)) * 100
    const progressPercentage = Math.round((currentStep / (totalSteps - 1)) * 100);

    const handleConfirmRegistrationWithUpdate = async () => {
        setSubmitting(true);
        try {
            const res = await updateUser({ fullName, email });
            if (!res || !res.success) {
                // stay on the same step; toast already shown in updateUser
                return;
            }
            // continue with your existing flow
            await handleConfirmRegistration({
                selectedWizard,
                phone,
                fullName,
                email,
                toast,
                currentStep,
                setCurrentStep,
                setSubmitting,
                urlWithoutProtocol,
            });
        } finally {
            setSubmitting(false);
        }
    };
    if (isLoading) {
        return (
            <div className='fixed inset-0 z-10 flex flex-col justify-center items-center bg-white dark:bg-primary-dark'>
                <LoaderSpin />
                <span className='animate-pulse mt-28 font-light text-sm'>
                    در حال بارگذاری اطلاعات کاربر...
                </span>
            </div>
        );
    }

    return (
        <>
            <div className='w-full flex flex-col justify-between items-center relative z-10 h-auto overflow-y-scroll'>
                {/* StepIndicator now gets the updated titles (with extra if needed) */}
                {currentStep > (hasExtraStep ? 1 : 0) && (
                    <StepIndicator
                        hasExtraStep={hasExtraStep}
                        selectedWizard={selectedWizard}
                        stepTitles={stepTitles}
                        currentStep={currentStep}
                        onPrevious={handlePreviousButtonClick}
                        progressPercentage={progressPercentage}
                        isLastStep={currentStep === totalSteps - 1}
                    />
                )}

                {/* 4. Extra “static” step #0 (if hasExtraStep) */}
                {selectedWizard == 'wizard2' ? (
                    <ExtraStaticStep2
                        percent={percent}
                        timer={timer}
                        showGroupFieldsBeforeMobile={showGroupFieldsBeforeMobile}
                        currentStep={currentStep}
                        onNextStep={() => setCurrentStep(1)}
                    />
                ) : (
                    <ExtraStaticStep
                        percent={percent}
                        timer={timer}
                        showGroupFieldsBeforeMobile={showGroupFieldsBeforeMobile}
                        currentStep={currentStep}
                        onNextStep={() => setCurrentStep(1)}
                    />
                )}

                {/* 5. Map through survey items, but shift each idx by +1 if hasExtraStep */}
                <SurveyStepRenderer
                    handleConfirmClosePopup={handleConfirmClosePopup}
                    landingSlug={popupLandingSlug(selectedWizard, tracking)}
                    showGroupFieldsBeforeMobile={showGroupFieldsBeforeMobile}
                    selectedWizard={selectedWizard}
                    survey={survey}
                    user={user?.firstName}
                    currentStep={currentStep}
                    totalSteps={totalSteps}
                    hasExtraStep={hasExtraStep}
                    phone={phone}
                    isLoggedInGlobal={isLoggedInGlobal}
                    isEditing={isEditing}
                    submitting={submitting}
                    setIsEditing={setIsEditing}
                    setPhone={setPhone}
                    fullName={fullName}
                    email={email}
                    setEmail={setEmail}
                    setFullName={setFullName}
                    setHasRegistered={setHasRegistered}
                    handleStepButtonClick={handleStepButtonClick}
                    handleConfirmRegistration={handleConfirmRegistrationWithUpdate}
                    urlWithoutProtocol={urlWithoutProtocol}
                    onPrevious={handlePreviousButtonClick}
                    percent={percent}
                    timer={timer}
                />
            </div>
        </>
    );
};

export default SurveyRenderer;
